// /frontend/src/pages/checkout/PlaceOrderPage.jsx - Modified for RFQ
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Layout from '../../components/layout/Layout';
import CheckoutSteps from './CheckoutSteps';
import { FaCreditCard, FaMoneyCheck, FaUniversity, FaCheckCircle } from 'react-icons/fa';
import { cartService } from '../../services/cartService';
import { useCheckout } from '../../contexts/CheckoutContext';
import { isRFQModeEnabled, shouldHidePrices, getRFQTexts } from '../../config/rfqConfig';

const PlaceOrderPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { shippingAddress, paymentMethod, paymentDetails, createOrder } = useCheckout();
  
  // State for cart data
  const [cart, setCart] = useState({ items: [], totalItems: 0, totalPrice: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [placingOrder, setPlacingOrder] = useState(false);
  
  // RFQ Configuration
  const isRFQMode = isRFQModeEnabled();
  const hidePrices = shouldHidePrices();
  const rfqTexts = getRFQTexts();
  
  // Calculate prices (masih diperlukan untuk backend, tapi disembunyikan di UI)
  const itemsPrice = cart.totalPrice || 0;
  const shippingPrice = itemsPrice > 100 ? 0 : 10;
  const taxRate = 0.10;
  const taxPrice = itemsPrice * taxRate;
  const totalPrice = isRFQMode ? 0 : itemsPrice + shippingPrice + taxPrice; // Set 0 untuk RFQ
  
  useEffect(() => {
    console.log("PlaceOrderPage mounted - RFQ Mode:", isRFQMode);
  }, []);

  // Check if user has gone through previous steps
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/placeorder');
      return;
    }
    
    if (!shippingAddress) {
      navigate('/shipping');
      return;
    }
    
    // Fetch cart data
    const fetchCart = async () => {
      try {
        setLoading(true);
        const cartData = await cartService.getCart();
        
        setCart({
          ...cartData,
          items: cartData?.items || []
        });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching cart:', err);
        setError('Failed to load your cart. Please try again.');
        setLoading(false);
        setCart({ items: [], totalItems: 0, totalPrice: 0 });
      }
    };
    
    fetchCart();
  }, [isAuthenticated, navigate, shippingAddress, paymentMethod]);
  
  // Handle place order (modified for RFQ)
  const handlePlaceOrder = async () => {
    try {
      setPlacingOrder(true);
      
      // Create order items
      const orderItems = cart.items.map(item => ({
        product: item.product._id,
        name: item.product.name,
        quantity: item.quantity,
        price: isRFQMode ? 0 : (item.product.isOnSale ? item.product.salePrice : item.product.price), // Set price 0 untuk RFQ
        variants: item.variants || {},
        image: item.product.images && item.product.images.length > 0 ? item.product.images[0] : ''
      }));
      
      // Create order with context (modified pricing for RFQ)
      const createdOrder = await createOrder(
        orderItems,
        isRFQMode ? 0 : itemsPrice,    // itemsPrice
        isRFQMode ? 0 : taxPrice,      // taxPrice  
        isRFQMode ? 0 : shippingPrice, // shippingPrice
        totalPrice                      // totalPrice (0 for RFQ)
      );
      
      // Clear cart
      await cartService.clearCart();
      
      // Navigate to confirmation
      if (createdOrder && createdOrder.order._id) {
        navigate(`/order/confirmation/${createdOrder.order._id}`);
      } else {
        console.error('Order created but no ID returned');
        setError('Order was created but details are unavailable. Please check your orders page.');
        setPlacingOrder(false);
      }
      
    } catch (err) {
      console.error('Error placing order:', err);
      setError(err.message || `There was a problem creating your ${isRFQMode ? 'quote request' : 'order'}. Please try again.`);
      setPlacingOrder(false);
    }
  };
  
  // Format payment method for display (hidden in RFQ mode)
  const getPaymentMethodDisplay = () => {
    if (isRFQMode) {
      return {
        icon: <FaCheckCircle className="text-teal-800" />,
        name: 'Quote Request',
        details: 'Payment details will be provided with quote'
      };
    }
    
    switch (paymentMethod) {
      case 'credit_card':
        return {
          icon: <FaCreditCard className="text-teal-800" />,
          name: 'Credit Card',
          details: paymentDetails?.cardNumber ? `Card ending in ${paymentDetails.cardNumber.slice(-4)}` : 'Credit Card'
        };
      case 'cheque':
        return {
          icon: <FaMoneyCheck className="text-teal-800" />,
          name: 'Cheque',
          details: paymentDetails?.chequeNumber ? `Cheque #${paymentDetails.chequeNumber}` : 'Cheque Payment'
        };
      case 'wire_transfer':
        return {
          icon: <FaUniversity className="text-teal-800" />,
          name: 'Wire Transfer',
          details: paymentDetails?.bankName ? `From ${paymentDetails.bankName}` : 'Wire Transfer'
        };
      default:
        return {
          icon: <FaCheckCircle className="text-teal-800" />,
          name: 'Other',
          details: 'Payment details'
        };
    }
  };
  
  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <CheckoutSteps step="placeorder" />
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="h-48 bg-gray-200 rounded mb-6"></div>
            <div className="h-32 bg-gray-200 rounded w-1/3 ml-auto"></div>
          </div>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <CheckoutSteps step="placeorder" />
        
        {error && (
          <div className="bg-red-100 text-red-700 p-4 rounded-md mb-6">
            {error}
          </div>
        )}
        
        {/* RFQ Mode Banner */}
        {isRFQMode && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="h-6 w-6 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div>
                <h3 className="font-medium text-blue-800">Request for Quotation Mode</h3>
                <p className="text-blue-700 text-sm">You are requesting a custom quote for the selected items. No payment is required at this time.</p>
              </div>
            </div>
          </div>
        )}
        
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {isRFQMode ? 'Quote Request Summary' : 'Order Summary'}
            </h1>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
              {/* Shipping Information */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-2">Shipping</h2>
                <div className="text-gray-700">
                  <p className="font-medium">{shippingAddress.fullName}</p>
                  <p>{shippingAddress.address}</p>
                  <p>{shippingAddress.city}, {shippingAddress.state} {shippingAddress.postalCode}</p>
                  <p>{shippingAddress.country}</p>
                  <p className="mt-1">Phone: {shippingAddress.phone}</p>
                </div>
              </div>
              
              {/* Payment Method - Hidden or modified in RFQ mode */}
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-2">
                  {isRFQMode ? 'Quote Request' : 'Payment'}
                </h2>
                <div className="flex items-center">
                  {getPaymentMethodDisplay().icon}
                  <div className="ml-2">
                    <p className="font-medium">{getPaymentMethodDisplay().name}</p>
                    <p className="text-gray-600 text-sm">{getPaymentMethodDisplay().details}</p>
                  </div>
                </div>
                
                {isRFQMode && (
                  <div className="text-blue-600 text-sm mt-2">
                    Payment instructions will be provided with your custom quote.
                  </div>
                )}
              </div>
              
              {/* Order Items - Modified table for RFQ */}
              <div className="col-span-1 md:col-span-2 mt-4">
                <h2 className="text-lg font-medium text-gray-900 mb-3">
                  {isRFQMode ? 'Items for Quote' : 'Order Items'}
                </h2>
                {cart.items.length === 0 ? (
                  <div className="text-gray-600">Your cart is empty</div>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Product
                          </th>
                          {!hidePrices && (
                            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Price
                            </th>
                          )}
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Quantity
                          </th>
                          {!hidePrices && (
                            <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Total
                            </th>
                          )}
                          {isRFQMode && (
                            <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Quote Status
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {cart.items.map((item) => {
                          if (!item.product) return null;
                          
                          const price = item.product.isOnSale 
                            ? item.product.salePrice 
                            : item.product.price;
                          
                          return (
                            <tr key={item._id}>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="h-12 w-12 bg-gray-100 rounded overflow-hidden mr-3">
                                    {item.product.images && item.product.images.length > 0 ? (
                                      <img 
                                        src={item.product.images[0]} 
                                        alt={item.product.name} 
                                        className="h-full w-full object-cover"
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src = '/placeholder-image.jpg';
                                        }}
                                      />
                                    ) : (
                                      <div className="h-full w-full flex items-center justify-center text-gray-400">
                                        No Image
                                      </div>
                                    )}
                                  </div>
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">{item.product.name}</div>
                                    {item.variants && Object.keys(item.variants).length > 0 && (
                                      <div className="text-xs text-gray-500">
                                        {Object.entries(item.variants).map(([key, value]) => (
                                          <div key={key}>{key}: {value}</div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              {!hidePrices && (
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                  ${price.toFixed(2)}
                                </td>
                              )}
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                {item.quantity}
                              </td>
                              {!hidePrices && (
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                                  ${(price * item.quantity).toFixed(2)}
                                </td>
                              )}
                              {isRFQMode && (
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                    Pending Quote
                                  </span>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Order Summary - Modified for RFQ */}
          <div className="bg-gray-50 p-6">
            <div className="max-w-md ml-auto">
              {!isRFQMode ? (
                // Normal pricing display
                <>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">Order Total</h2>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Items:</span>
                      <span className="font-medium">${itemsPrice.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Shipping:</span>
                      <span className="font-medium">
                        {shippingPrice === 0 ? 'Free' : `${shippingPrice.toFixed(2)}`}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tax (10%):</span>
                      <span className="font-medium">${taxPrice.toFixed(2)}</span>
                    </div>
                    <div className="pt-3 border-t border-gray-200 flex justify-between text-lg font-bold">
                      <span>Total:</span>
                      <span>${totalPrice.toFixed(2)}</span>
                    </div>
                  </div>
                </>
              ) : (
                // RFQ Mode Display
                <>
                  <h2 className="text-lg font-medium text-gray-900 mb-4">{rfqTexts.orderSummaryTitle}</h2>
                  
                  <div className="bg-blue-50 p-4 rounded-lg mb-4">
                    <div className="flex items-center mb-2">
                      <svg className="h-5 w-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <h3 className="font-medium text-blue-800">Custom Quote Request</h3>
                    </div>
                    <p className="text-blue-700 text-sm">
                      You are requesting a custom quotation for the selected items. 
                      Our team will review your requirements and provide a detailed quote within 24-48 hours.
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Items:</span>
                      <span className="font-medium">{cart.items.length} item(s)</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Quantity:</span>
                      <span className="font-medium">{cart.items.reduce((total, item) => total + item.quantity, 0)}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-gray-600">Quote Status:</span>
                      <span className="font-medium text-orange-600">{rfqTexts.statusPending}</span>
                    </div>
                    
                    <div className="pt-3 border-t border-gray-200 flex justify-between text-lg font-bold">
                      <span>Price:</span>
                      <span className="text-blue-600">{rfqTexts.totalPriceLabel}</span>
                    </div>
                  </div>
                </>
              )}
              
              <div className="mt-6 space-y-4">
                <button
                  onClick={handlePlaceOrder}
                  disabled={cart.items.length === 0 || placingOrder}
                  className={`w-full py-3 rounded-md font-medium ${
                    cart.items.length === 0 || placingOrder
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-teal-800 text-white hover:bg-teal-900'
                  } transition-colors`}
                >
                  {placingOrder 
                    ? 'Processing...' 
                    : isRFQMode 
                      ? rfqTexts.placeOrderButton
                      : 'Place Order'
                  }
                </button>
                
                {/* Informasi tambahan untuk RFQ */}
                {isRFQMode && (
                  <div className="text-xs text-gray-500 text-center">
                    By submitting this request, you agree to receive a custom quotation. 
                    No payment will be processed until you approve the quote.
                  </div>
                )}
                
                <div className="flex space-x-4">
                  <Link
                    to="/shipping"
                    className="w-1/2 py-2 text-center border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Back to Shipping
                  </Link>
                  
                  <Link
                    to="/cart"
                    className="w-1/2 py-2 text-center border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Edit Cart
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PlaceOrderPage;